import type { ComponentType } from "react";
import { useLocation } from "wouter";
import { cn } from "../../lib/cn";
import { SIM_ACTIVE } from "./flags";
import { useEnabledManifests } from "./registry";
import { SlotBoundary } from "./SlotBoundary";
import { isSignedOutPath, isSimSurfaceAllowed } from "./surface";
import { HeaderNav } from "./nav/HeaderNav";
import { BottomNav } from "./nav/BottomNav";
import type { SlotEntry, SlotName, SlotPropsMap } from "./types";

type FeatureSlotProps<N extends SlotName> = {
  name: N;
  /** Wrapper classes (spacing). The wrapper exists only when something renders. */
  className?: string;
} & SlotPropsMap[N];

/** Slots the kit fills itself, from the enabled manifests' navItems. */
const BUILT_IN: Partial<Record<SlotName, SlotEntry>> = {
  "header.nav": { id: "_sim.headerNav", order: 0, component: HeaderNav },
  "shell.bottomNav": { id: "_sim.bottomNav", order: 0, component: BottomNav },
};

/**
 * Renders every enabled feature's entries for one slot, in `order`. Renders
 * nothing — not even its wrapper — when the simulation is off, on a protected
 * surface (/care, /intake, the EIF), or when no entry is enabled.
 */
export function FeatureSlot<N extends SlotName>(props: FeatureSlotProps<N>) {
  const { name, className, ...rest } = props;
  const [path] = useLocation();
  const manifests = useEnabledManifests();
  if (!SIM_ACTIVE || !isSimSurfaceAllowed(path)) return null;
  if (name === "app.overlay" && isSignedOutPath(path)) return null;

  const builtIn = BUILT_IN[name];
  const entries = [
    ...(builtIn ? [builtIn] : []),
    ...manifests.flatMap((m) => (m.slots?.[name] ?? []) as SlotEntry[]),
  ].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  if (entries.length === 0) return null;

  const items = entries.map(({ id, component }) => {
    const Entry = component as ComponentType<Record<string, unknown>>;
    return (
      <SlotBoundary key={id}>
        <Entry {...rest} />
      </SlotBoundary>
    );
  });
  return className ? <div className={cn("empty:hidden", className)}>{items}</div> : <>{items}</>;
}
